import { useState, useRef, useCallback, useEffect } from 'react'
import { Maximize2, X, Crop as CropIcon, Check, RotateCcw } from 'lucide-react'

/**
 * MediaPreview
 *
 * A small, reusable preview block for the right-side Properties panels so the
 * selected image / video / audio element can be viewed and played inline, with
 * a "view fullsize" overlay and (for images) an interactive CROP tool.
 *
 * The media URL lives on different fields depending on how the element was
 * created, so we resolve it defensively:
 *   image -> element.src || element.url || element.storageUrl
 *   video -> element.src || element.file?.url || element.url
 *   audio -> element.file?.url || element.src || element.url
 *
 * Crop model: element.crop = { x, y, width, height } — all normalized 0..1
 * fractions of the natural image. Absent/null means no crop (full image).
 */

export function resolveMediaUrl(element) {
  if (!element) return null
  const type = element.type
  if (type === 'image') {
    return element.src || element.url || element.storageUrl || null
  }
  if (type === 'video') {
    return element.src || (element.file && element.file.url) || element.url || null
  }
  if (type === 'audio' || type === 'tts') {
    return (element.file && element.file.url) || element.tts_generated_link || element.src || element.url || null
  }
  return element.src || element.url || (element.file && element.file.url) || null
}

const clamp01 = (v) => Math.max(0, Math.min(1, v))

const MediaPreview = ({ element, selectedElement, updateElementProperty }) => {
  const [fullscreen, setFullscreen] = useState(false)
  const [cropping, setCropping] = useState(false)
  // Working crop rect (normalized 0..1) while in crop mode.
  const [draft, setDraft] = useState(null)
  const imgWrapRef = useRef(null)
  // Drag state: { mode:'move'|'new'|'nw'|'ne'|'sw'|'se', startX, startY, orig }
  const dragRef = useRef(null)

  if (!element) return null

  const type = element.type
  const url = resolveMediaUrl(element)

  const isImage = type === 'image'
  const isVideo = type === 'video'
  const isAudio = type === 'audio' || type === 'tts'
  if (!isImage && !isVideo && !isAudio) return null

  if (!url) {
    return (
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
        <p className="text-xs text-gray-400">No media source available for this element.</p>
      </div>
    )
  }

  const canFullscreen = isImage || isVideo
  const canCrop = isImage && typeof updateElementProperty === 'function' && selectedElement
  const existingCrop = element.crop && typeof element.crop === 'object' ? element.crop : null

  const beginCrop = () => {
    setDraft(existingCrop
      ? { x: existingCrop.x || 0, y: existingCrop.y || 0, width: existingCrop.width || 1, height: existingCrop.height || 1 }
      : { x: 0, y: 0, width: 1, height: 1 })
    setCropping(true)
  }

  const cancelCrop = () => {
    setCropping(false)
    setDraft(null)
    dragRef.current = null
  }

  const applyCrop = () => {
    if (!draft) { cancelCrop(); return }
    // Normalize: ensure positive size; a near-full crop clears the crop.
    const c = {
      x: clamp01(draft.x),
      y: clamp01(draft.y),
      width: clamp01(draft.width),
      height: clamp01(draft.height),
    }
    const isFull = c.x <= 0.001 && c.y <= 0.001 && c.width >= 0.999 && c.height >= 0.999
    updateElementProperty(selectedElement, 'crop', isFull ? null : c)
    cancelCrop()
  }

  const resetCrop = () => {
    setDraft({ x: 0, y: 0, width: 1, height: 1 })
  }

  // --- Drag handling (pointer events in normalized space) ------------------
  const pointToNorm = (e) => {
    const rect = imgWrapRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0 }
    return {
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01((e.clientY - rect.top) / rect.height),
    }
  }

  const onPointerDown = (mode) => (e) => {
    if (!cropping) return
    e.preventDefault()
    e.stopPropagation()
    const p = pointToNorm(e)
    dragRef.current = { mode, start: p, orig: { ...draft } }
    try { e.target.setPointerCapture?.(e.pointerId) } catch { /* ignore */ }
  }

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current
    if (!d || !cropping) return
    const rect = imgWrapRef.current?.getBoundingClientRect()
    if (!rect) return
    const px = clamp01((e.clientX - rect.left) / rect.width)
    const py = clamp01((e.clientY - rect.top) / rect.height)
    const dx = px - d.start.x
    const dy = py - d.start.y
    const o = d.orig
    let next = { ...o }

    if (d.mode === 'move') {
      next.x = clamp01(o.x + dx)
      next.y = clamp01(o.y + dy)
      // keep rect inside bounds
      next.x = Math.min(next.x, 1 - o.width)
      next.y = Math.min(next.y, 1 - o.height)
    } else if (d.mode === 'new') {
      const x0 = Math.min(d.start.x, px)
      const y0 = Math.min(d.start.y, py)
      next = { x: x0, y: y0, width: Math.abs(px - d.start.x), height: Math.abs(py - d.start.y) }
    } else {
      // resize via a corner handle
      let left = o.x, top = o.y, right = o.x + o.width, bottom = o.y + o.height
      if (d.mode.includes('w')) left = clamp01(o.x + dx)
      if (d.mode.includes('n')) top = clamp01(o.y + dy)
      if (d.mode.includes('e')) right = clamp01(o.x + o.width + dx)
      if (d.mode.includes('s')) bottom = clamp01(o.y + o.height + dy)
      next = {
        x: Math.min(left, right),
        y: Math.min(top, bottom),
        width: Math.abs(right - left),
        height: Math.abs(bottom - top),
      }
    }
    // enforce a small minimum size
    if (next.width < 0.02) next.width = 0.02
    if (next.height < 0.02) next.height = 0.02
    setDraft(next)
  }, [cropping])

  const onPointerUp = useCallback(() => { dragRef.current = null }, [])

  // Global listeners while cropping so a drag that leaves the box still tracks.
  useEffect(() => {
    if (!cropping) return
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
    }
  }, [cropping, onPointerMove, onPointerUp])

  // For the inline preview (when NOT cropping) show the cropped region if set,
  // using a scale/translate so the crop fills the preview box.
  const croppedImgStyle = (() => {
    if (cropping || !existingCrop) return {}
    const cw = existingCrop.width || 1
    const ch = existingCrop.height || 1
    return {
      transform: `scale(${1 / cw}, ${1 / ch}) translate(${-existingCrop.x * 100}%, ${-existingCrop.y * 100}%)`,
      transformOrigin: 'top left',
    }
  })()

  const pct = (v) => `${(v * 100).toFixed(2)}%`

  return (
    <div className="bg-gray-50 p-3 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Preview</h3>
        <div className="flex items-center gap-2">
          {canCrop && !cropping && (
            <button
              type="button"
              onClick={beginCrop}
              title="Crop image"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
            >
              <CropIcon className="w-3.5 h-3.5" /> {existingCrop ? 'Edit crop' : 'Crop'}
            </button>
          )}
          {canFullscreen && !cropping && (
            <button
              type="button"
              onClick={() => setFullscreen(true)}
              title="View full size"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
            >
              <Maximize2 className="w-3.5 h-3.5" /> Full size
            </button>
          )}
        </div>
      </div>

      <div className="rounded-md overflow-hidden bg-gray-900 flex items-center justify-center relative" style={{ maxHeight: 220 }}>
        {isImage && !cropping && (
          <div className="w-full overflow-hidden" style={{ maxHeight: 220 }}>
            <img
              src={url}
              alt={element.name || 'Preview'}
              className="max-h-[220px] w-full object-contain cursor-zoom-in"
              style={croppedImgStyle}
              onClick={() => setFullscreen(true)}
              draggable={false}
            />
          </div>
        )}

        {/* Crop editor */}
        {isImage && cropping && draft && (
          <div
            ref={imgWrapRef}
            className="relative w-full select-none"
            style={{ maxHeight: 220, touchAction: 'none' }}
          >
            {/* Base image (dimmed) with a "new rect" drag surface */}
            <img
              src={url}
              alt="Crop"
              className="max-h-[220px] w-full object-contain opacity-60"
              draggable={false}
              onPointerDown={onPointerDown('new')}
            />
            {/* Selected crop region (bright) */}
            <div
              className="absolute border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] cursor-move"
              style={{ left: pct(draft.x), top: pct(draft.y), width: pct(draft.width), height: pct(draft.height) }}
              onPointerDown={onPointerDown('move')}
            >
              {/* Corner handles */}
              {['nw', 'ne', 'sw', 'se'].map((corner) => (
                <span
                  key={corner}
                  onPointerDown={onPointerDown(corner)}
                  className="absolute w-3 h-3 bg-white border border-blue-500 rounded-sm"
                  style={{
                    left: corner.includes('w') ? -6 : undefined,
                    right: corner.includes('e') ? -6 : undefined,
                    top: corner.includes('n') ? -6 : undefined,
                    bottom: corner.includes('s') ? -6 : undefined,
                    cursor: `${corner}-resize`,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {isVideo && (
          <video
            src={url}
            poster={element.thumbnail_url || element.thumbnailUrl || undefined}
            controls
            preload="metadata"
            className="max-h-[220px] w-full object-contain bg-black"
          />
        )}
        {isAudio && (
          <div className="w-full p-3 bg-white">
            {element.name || (element.file && element.file.name) ? (
              <p className="text-xs text-gray-600 mb-2 truncate" title={element.name || element.file?.name}>
                {element.name || element.file?.name}
              </p>
            ) : null}
            <audio src={url} controls preload="metadata" className="w-full" />
          </div>
        )}
      </div>

      {/* Crop controls */}
      {cropping && (
        <div className="mt-2 flex items-center justify-between">
          <p className="text-[11px] text-gray-500">Drag a box or the handles to set the crop.</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={resetCrop}
              className="flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 text-gray-600"
              title="Reset to full image"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
            <button
              type="button"
              onClick={cancelCrop}
              className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 text-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyCrop}
              className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Check className="w-3.5 h-3.5" /> Apply crop
            </button>
          </div>
        </div>
      )}

      {/* Fullsize overlay */}
      {fullscreen && canFullscreen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[1000] p-6"
          onClick={() => setFullscreen(false)}
        >
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {isImage && (
              <img src={url} alt={element.name || 'Preview'} className="max-w-[90vw] max-h-[90vh] object-contain" />
            )}
            {isVideo && (
              <video
                src={url}
                poster={element.thumbnail_url || element.thumbnailUrl || undefined}
                controls
                autoPlay
                className="max-w-[90vw] max-h-[90vh] object-contain bg-black"
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MediaPreview
